import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  ClipboardText,
  ChatCircle,
  Buildings,
  CalendarBlank,
  ArrowsClockwise,
  Paperclip,
  PencilSimple,
  Trash,
  ArrowUp,
  Upload,
  CheckCircle,
  XCircle,
  Question,
} from "@phosphor-icons/react";

const ICON_MAP = {
  "company.created": { icon: Buildings, color: "text-slate-600 bg-slate-100" },
  "cycle.created": { icon: CalendarBlank, color: "text-purple-700 bg-purple-100" },
  "cycle.rollforward": { icon: ArrowsClockwise, color: "text-blue-700 bg-blue-100" },
  "observation.created": { icon: ClipboardText, color: "text-emerald-700 bg-emerald-100" },
  "observation.updated": { icon: PencilSimple, color: "text-amber-700 bg-amber-100" },
  "observation.status_changed": { icon: ArrowUp, color: "text-blue-700 bg-blue-100" },
  "observation.deleted": { icon: Trash, color: "text-red-700 bg-red-100" },
  "observation.bulk_imported": { icon: Upload, color: "text-blue-700 bg-blue-100" },
  "resolution.submitted": { icon: ChatCircle, color: "text-blue-700 bg-blue-100" },
  "resolution.reviewed": { icon: CheckCircle, color: "text-emerald-700 bg-emerald-100" },
  "attachment.uploaded": { icon: Paperclip, color: "text-slate-700 bg-slate-100" },
  "attachment.deleted": { icon: Trash, color: "text-red-700 bg-red-100" },
};

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 7 * 86400) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function ActivityTimeline({ companyId, cycleId, observationId, compact }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ company_id: companyId });
      if (cycleId) qs.set("cycle_id", cycleId);
      if (observationId) qs.set("observation_id", observationId);
      const r = await api.get(`/activities?${qs.toString()}`);
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) load();
    // eslint-disable-next-line
  }, [companyId, cycleId, observationId]);

  useEffect(() => {
    const h = () => load();
    window.addEventListener("activities-reload", h);
    return () => window.removeEventListener("activities-reload", h);
    // eslint-disable-next-line
  }, [companyId, cycleId, observationId]);

  if (loading) {
    return (
      <div className="space-y-2" data-testid="activity-timeline-loading">
        <div className="h-3 bg-slate-100 rounded ai-shimmer-bar w-1/2" />
        <div className="h-3 bg-slate-100 rounded ai-shimmer-bar w-2/3" />
        <div className="h-3 bg-slate-100 rounded ai-shimmer-bar w-1/3" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-sm text-slate-500 py-6 text-center" data-testid="activity-empty">
        No activity yet. Actions you take here will appear in this audit log.
      </div>
    );
  }

  return (
    <ol className="relative" data-testid="activity-timeline">
      <div className="absolute left-[15px] top-2 bottom-2 w-px bg-slate-200" />
      {items.map((a, idx) => {
        const m = ICON_MAP[a.action] || { icon: Question, color: "text-slate-600 bg-slate-100" };
        const Icon = m.icon;
        return (
          <li key={a.id} className="relative pl-10 pb-4 last:pb-0" data-testid={`activity-${a.id}`}>
            <div className={`absolute left-0 top-0 w-8 h-8 rounded-full ${m.color} flex items-center justify-center ring-4 ring-white`}>
              <Icon size={14} weight="bold" />
            </div>
            <div className={`bg-white border border-slate-200 rounded-md p-3 ${compact ? "py-2" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm text-slate-800 leading-snug">{a.summary}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 shrink-0">{fmtTime(a.created_at)}</div>
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                <span className={`font-medium ${a.actor_role === "audit_team" ? "text-slate-700" : "text-blue-700"}`}>
                  {a.actor_name}
                </span>
                <span className="mx-1.5">·</span>
                <span>{a.actor_role === "audit_team" ? "Auditor" : "Client"}</span>
                <span className="mx-1.5">·</span>
                <span className="mono">{a.action}</span>
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
